import React, { useState, useEffect } from 'react';
import Logo from './components/Logo';
import './DDFTestPage.css';
import { filterPivotRows } from './utils/pivotUtils';

const DDFTestPage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchCount, setFetchCount] = useState(0);
  const [responseData, setResponseData] = useState(null);

  // Property lookup state
  const [propertyKey, setPropertyKey] = useState('');
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [propertyError, setPropertyError] = useState(null);
  
  // Agent lookup state
  const [agentDetails, setAgentDetails] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState(null);

  // Pivot table cell selection state
  const [selectedCellInfo, setSelectedCellInfo] = useState(null);
  const [cellListingsLoading, setCellListingsLoading] = useState(false);



  // Full database analysis state with polling support
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [analysisTaskId, setAnalysisTaskId] = useState(null);

  // Listing details pivot table state
  const [pivotData, setPivotData] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  // Property types are now pivot table rows, not filters
  
  // Enhanced filtering state
  const [postalCodeFilter, setPostalCodeFilter] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [allFilterOptions, setAllFilterOptions] = useState(null);

  const fetchDDFListings = async () => {
    setLoading(true);
    setError(null);
    setSelectedCellInfo(null); // Clear cell selection when fetching new listings
    
    try {
      // Call our Lambda function's DDF test endpoint
      const response = await fetch('https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/ddf-test', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setListings(data.listings || []);
        setFetchCount(data.count || 0);
        setResponseData(data);
      } else {
        setError(`API Error (${response.status}): ${data.error || 'Failed to fetch listings'}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}. Check browser console for details.`);
      console.error('DDF Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchListingDetailsPivot = async (filters = {}) => {
    setPivotLoading(true);
    setPivotError(null);
    setSelectedCellInfo(null); // Clear cell selection when fetching new pivot data
    
    try {
      // Build URL with active filters (no property type filter)
      const searchParams = new URLSearchParams();
      
      // Location filters - postal code takes precedence over city/area
      const postalCode = filters.postalCode || postalCodeFilter;
      if (postalCode && postalCode.trim().length >= 3) {
        // Use ONLY postal code filter (independent of city/area)
        searchParams.append('area_postal_code', postalCode.trim().toUpperCase());
      } else {
        // Use city + area filters (only when no postal code)
        const city = filters.city || selectedCity;
        if (city) {
          searchParams.append('city', city);
        }
        
        const area = filters.area || selectedArea;
        if (area) {
          searchParams.append('neighborhood', area);
        }
      }
      
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${searchParams}`;
      console.log('Fetching pivot data with filters:', Object.fromEntries(searchParams));

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setPivotData(data.data);
        
        // Update filter options from API response
        if (data.data.filter_options) {
          setAllFilterOptions(data.data.filter_options);
          setAvailableCities(data.data.filter_options.cities || []);
          
          // Update available areas - backend now filters by city automatically
          setAvailableAreas(data.data.filter_options.neighborhoods || []);
        }
        
        console.log('Enhanced pivot data loaded:', data.data);
      } else {
        setPivotError(`API Error (${response.status}): ${data.error || 'Failed to fetch pivot data'}`);
      }
    } catch (err) {
      setPivotError(`Network error: ${err.message}. Check browser console for details.`);
      console.error('Pivot Fetch Error:', err);
    } finally {
      setPivotLoading(false);
    }
  };

  const fetchPropertyDetails = async () => {
    if (!propertyKey.trim()) {
      setPropertyError('Please enter a property key');
      return;
    }

    const requestedKey = propertyKey.trim();
    console.log('🔍 Requesting property details for key:', requestedKey);

    setPropertyLoading(true);
    setPropertyError(null);
    setPropertyDetails(null);
    setAgentDetails(null); // Reset agent details when fetching new property
    
    try {
      // Call the DDF test endpoint with property_id parameter
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/ddf-test?property_id=${encodeURIComponent(requestedKey)}`;
      console.log('🌐 Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      console.log('📡 Response received:', data);
      
      if (response.ok && data.success) {
        const returnedKey = data.property_details?.ListingKey;
        console.log('🔑 Property key verification:', {
          requested: requestedKey,
          returned: returnedKey,
          match: String(requestedKey) === String(returnedKey)
        });
        
        if (String(requestedKey) !== String(returnedKey)) {
          console.error('❌ Property key mismatch detected!');
          setPropertyError(`Property key mismatch: requested ${requestedKey} but got ${returnedKey}`);
          return;
        }
        
        setPropertyDetails(data);
        
        // Automatically fetch agent details if ListAgentKey is available
        const listAgentKey = data.property_details?.ListAgentKey;
        if (listAgentKey) {
          fetchAgentDetails(listAgentKey);
        }
      } else {
        setPropertyError(`API Error (${response.status}): ${data.error || 'Failed to fetch property details'}`);
      }
    } catch (err) {
      setPropertyError(`Network error: ${err.message}`);
      console.error('Property Fetch Error:', err);
    } finally {
      setPropertyLoading(false);
    }
  };

  const fetchAgentDetails = async (memberKey) => {
    if (!memberKey) {
      setAgentError('No agent member key provided');
      return;
    }

    setAgentLoading(true);
    setAgentError(null);
    setAgentDetails(null);
    
    try {
      // Call the DDF test endpoint with member_id parameter for agent lookup
      const response = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/ddf-test?member_id=${encodeURIComponent(memberKey)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setAgentDetails(data);
      } else {
        setAgentError(`API Error (${response.status}): ${data.error || 'Failed to fetch agent details'}`);
      }
    } catch (err) {
      setAgentError(`Network error: ${err.message}`);
      console.error('Agent Fetch Error:', err);
    } finally {
      setAgentLoading(false);
    }
  };

  const fetchFullDatabaseAnalysis = async () => {
    setAnalysisLoading(true);
    setAnalysisError(null);
    setAnalysisData(null);
    setAnalysisProgress(null);
    setAnalysisTaskId(null);
    
    try {
      // Start the async analysis operation
      const startResponse = await fetch('https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/ddf-test?fetch_all=true', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const startData = await startResponse.json();
      console.log('Analysis start response:', startData);
      
      if (startResponse.ok && startData.success && startData.task_id) {
        const taskId = startData.task_id;
        setAnalysisTaskId(taskId);
        setAnalysisProgress({
          status: 'starting',
          message: 'DDF fetch operation started...',
          task_id: taskId
        });
        
        // Start polling for progress
        pollAnalysisStatus(taskId);
      } else {
        setAnalysisError(`Failed to start analysis: ${startData.error || 'Unknown error'}`);
        setAnalysisLoading(false);
      }
    } catch (err) {
      setAnalysisError(`Network error starting analysis: ${err.message}`);
      console.error('Analysis Start Error:', err);
      setAnalysisLoading(false);
    }
  };

  const pollAnalysisStatus = async (taskId, retryCount = 0) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const statusResponse = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/ddf-test?fetch_all=true&task_id=${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const statusData = await statusResponse.json();
      console.log('Analysis status response:', statusData);
      
      if (statusResponse.ok && statusData.success && statusData.status) {
        const status = statusData.status;
        
        // Reset retry count on successful response
        retryCount = 0;
        
        // Update progress display
        setAnalysisProgress({
          ...status,
          task_id: taskId
        });
        
        // Check if operation is complete
        if (status.status === 'completed') {
          console.log('Analysis completed successfully!');
          setAnalysisData({
            ...statusData,
            task_id: taskId,
            elapsed_time: status.elapsed_seconds
          });
          setAnalysisLoading(false);
        } else if (status.status === 'error') {
          console.error('Analysis failed:', status.error);
          setAnalysisError(`Analysis failed: ${status.error}`);
          setAnalysisLoading(false);
        } else {
          // Continue polling - operation is still in progress
          // Use progressive back-off: start with 3 seconds, increase to 5 seconds after some time
          const pollInterval = status.elapsed_seconds > 60 ? 5000 : 3000;
          setTimeout(() => pollAnalysisStatus(taskId, 0), pollInterval);
        }
      } else if (statusResponse.status === 410 && statusData.error_code === 'TASK_LOST') {
        // Handle task lost due to server recycling
        console.warn('Analysis task was lost due to server recycling:', statusData.message);
        setAnalysisError(
          `${statusData.message} The server was recycled during processing. Please click "Retry Full Analysis" to restart.`
        );
        setAnalysisLoading(false);
        setAnalysisProgress(null);
        setAnalysisTaskId(null);
      } else {
        console.error('Failed to get status:', statusData);
        setAnalysisError(`Failed to get analysis status: ${statusData.error || 'Unknown error'}`);
        setAnalysisLoading(false);
      }
    } catch (err) {
      console.error('Error polling status:', err);
      
      // Handle network errors with retry logic
      if (err.name === 'AbortError') {
        console.log('Status request timed out, retrying...');
      }
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const retryDelay = Math.min(5000 * Math.pow(2, retryCount), 15000); // Max 15 second delay
        console.log(`Retrying in ${retryDelay/1000} seconds... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => pollAnalysisStatus(taskId, retryCount + 1), retryDelay);
      } else {
        setAnalysisError(`Error checking analysis status: Failed to fetch`);
        setAnalysisLoading(false);
      }
    }
  };

  const cancelAnalysis = () => {
    setAnalysisLoading(false);
    setAnalysisProgress(null);
    setAnalysisTaskId(null);
    console.log('Analysis cancelled by user');
  };

  useEffect(() => {
    // Auto-fetch listings and pivot data when component mounts
    fetchDDFListings();
    fetchListingDetailsPivot();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    const numPrice = parseInt(price);
    return `$${numPrice.toLocaleString()}`;
  };

  const handleCellClick = async (propertyType, bedroomCount, cellData) => {
    if (cellData && cellData.count > 0) {
      setCellListingsLoading(true);
      setSelectedCellInfo({
        propertyType,
        bedroomCount,
        count: cellData.count,
        averageRent: cellData.average_rent,
        minRent: cellData.min_rent,
        maxRent: cellData.max_rent
      });

      try {
        // Build search parameters for the property search endpoint
        const searchParams = new URLSearchParams({
          property_type: propertyType,
          bedrooms: bedroomCount,
          limit: '20' // Get up to 20 listings for the cell
        });

        // Add location filters - postal code takes precedence over city/area
        if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
          // Use ONLY postal code filter (independent of city/area)
          searchParams.append('postal_code', postalCodeFilter.trim().toUpperCase());
        } else {
          // Use city + area filters (only when no postal code)
          if (selectedCity) {
            searchParams.append('city', selectedCity);
          }
          if (selectedArea) {
            searchParams.append('neighborhood', selectedArea);
          }
        }

        // Call the new property search endpoint
        const response = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/property-search?${searchParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        const data = await response.json();
        
        if (response.ok && data.success) {
          // Convert search results to our display format
          const detailedListings = data.data.listings.map(listing => ({
            id: listing.id,
            ddf_listing_id: listing.origin_post_id, // Add explicit DDF listing ID for raw data modal
            address: listing.address || `${listing.neighborhood || 'Unknown'}, ${listing.city}`, // Use API address field
            city: listing.city,
            province: listing.province || 'ON',
            postal_code: listing.postal_code,
            neighborhood: listing.neighborhood,
            property_type: listing.property_type,
            bedrooms: listing.bedrooms?.toString() || '',
            bathrooms: listing.bathrooms?.toString() || '',
            parking_spaces: listing.parking_spaces?.toString() || '',
            size_interior: listing.size_interior || '',
            price: listing.price?.toString() || '',
            description: listing.description || 'No description available',
            date_listed: listing.available_from || '',
            furnished: listing.furnished,
            rental_term: listing.rental_term,
            pet_friendly: listing.pet_friendly,
            listing_url: listing.listing_url, // Add listing URL for REALTOR.ca link
            photos: listing.media_urls ? listing.media_urls.map((url, index) => ({
              url,
              order: index + 1,
              description: ''
            })) : [],
            agent_details: listing.agent ? {
              name: `${listing.agent.first_name || ''} ${listing.agent.last_name || ''}`.trim(),
              phone: listing.agent.phone,
              username: listing.agent.username,
              photo_url: listing.agent.photo_url,
              office_name: listing.agent.office_name
            } : null
          }));

          // Update the listings display
          setListings(detailedListings);
          setFetchCount(detailedListings.length);
          
          console.log(`Loaded ${detailedListings.length} listings for ${propertyType}, ${bedroomCount} bedrooms`);
          
          // Scroll to listings section
          setTimeout(() => {
            const listingsSection = document.querySelector('.listings-grid');
            if (listingsSection) {
              listingsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);

        } else {
          setError(`Failed to load listings: ${data.error || 'Unknown error'}`);
          console.error('Property search error:', data);
        }

      } catch (error) {
        console.error('Error fetching cell listings:', error);
        setError(`Network error: ${error.message}`);
      } finally {
        setCellListingsLoading(false);
      }
    }
  };





  const handlePostalCodeChange = (event) => {
    const newPostalCode = event.target.value;
    setPostalCodeFilter(newPostalCode);
    
    // Auto-filter when user types 3 or more characters
    if (newPostalCode.trim().length >= 3 || newPostalCode.trim().length === 0) {
      fetchListingDetailsPivot({ postalCode: newPostalCode });
    }
  };

  const handleCityChange = (event) => {
    const newCity = event.target.value;
    setSelectedCity(newCity);
    setSelectedArea(''); // Reset area selection when city changes
    
    // Clear available areas temporarily while fetching new data
    setAvailableAreas([]);
    
    // Fetch fresh pivot data with the new city filter
    // This will update allFilterOptions with city-specific neighborhoods
    fetchListingDetailsPivot({ city: newCity, area: '' });
  };

  const handleAreaChange = (event) => {
    const newArea = event.target.value;
    setSelectedArea(newArea);
    fetchListingDetailsPivot({ area: newArea });
  };

  const clearAllFilters = () => {
    setPostalCodeFilter('');
    setSelectedCity('');
    setSelectedArea('');
    setAvailableAreas([]);
    fetchListingDetailsPivot({
      postalCode: '',
      city: '',
      area: ''
    });
  };









  const formatJSON = (obj) => {
    // Convert object to formatted JSON string
    const jsonString = JSON.stringify(obj, null, 2);
    
    // Apply syntax highlighting with HTML spans
    return jsonString
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/(".*?")\s*:/g, '<span style="color: #4FC3F7;">$1</span>:') // Keys in blue
      .replace(/:\s*(".*?")/g, ': <span style="color: #A5D6A7;">$1</span>') // String values in green
      .replace(/:\s*(\d+\.?\d*)/g, ': <span style="color: #FFCC80;">$1</span>') // Numbers in orange
      .replace(/:\s*(true|false)/g, ': <span style="color: #F48FB1;">$1</span>') // Booleans in pink
      .replace(/:\s*(null)/g, ': <span style="color: #B0BEC5;">$1</span>') // null in grey
      .replace(/([{}[\]])/g, '<span style="color: #FFE082;">$1</span>') // Brackets in yellow
      .replace(/(,)$/gm, '<span style="color: #E0E0E0;">$1</span>'); // Commas in light grey
  };

  const ListingDetailsPivotTable = () => {
    if (!pivotData || !pivotData.pivot_table) {
      return <div>No pivot table data available</div>;
    }

    const { pivot_table, bedrooms } = pivotData;

    // Convert pivot_table to array for easier processing
    const rowEntries = filterPivotRows(
      Object.entries(pivot_table).map(([key, data]) => ({
        key,
        label: data.label,
        property_type: data.property_type,
        bedrooms: data.bedrooms
      }))
    );

    // Calculate totals
    const calculateTotals = () => {
      const rowTotals = {};
      const bedroomTotals = {};
      let grandTotal = { count: 0, totalRent: 0 };

      // Initialize bedroom totals
      bedrooms.forEach(bedroom => {
        bedroomTotals[bedroom] = { count: 0, totalRent: 0 };
      });

      // Calculate row and bedroom totals
      rowEntries.forEach(row => {
        rowTotals[row.key] = { count: 0, totalRent: 0 };
        
        bedrooms.forEach(bedroom => {
          const cellData = row.bedrooms[bedroom];
          if (cellData && cellData.count > 0) {
            const totalRentForCell = cellData.average_rent * cellData.count;
            
            // Add to row total
            rowTotals[row.key].count += cellData.count;
            rowTotals[row.key].totalRent += totalRentForCell;
            
            // Add to bedroom total
            bedroomTotals[bedroom].count += cellData.count;
            bedroomTotals[bedroom].totalRent += totalRentForCell;
            
            // Add to grand total
            grandTotal.count += cellData.count;
            grandTotal.totalRent += totalRentForCell;
          }
        });
        
        // Calculate average for row
        if (rowTotals[row.key].count > 0) {
          rowTotals[row.key].average = rowTotals[row.key].totalRent / rowTotals[row.key].count;
        }
      });

      // Calculate averages for bedrooms
      bedrooms.forEach(bedroom => {
        if (bedroomTotals[bedroom].count > 0) {
          bedroomTotals[bedroom].average = bedroomTotals[bedroom].totalRent / bedroomTotals[bedroom].count;
        }
      });

      // Calculate grand total average
      if (grandTotal.count > 0) {
        grandTotal.average = grandTotal.totalRent / grandTotal.count;
      }

      return { rowTotals, bedroomTotals, grandTotal };
    };

    const { rowTotals, bedroomTotals, grandTotal } = calculateTotals();

    // Get active filter summary for display
    const getFilterSummary = () => {
      const filters = [];
      if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
        // Postal code mode
        filters.push(`Postal Code: ${postalCodeFilter.trim().toUpperCase()}`);
      } else {
        // City/Area mode
        if (selectedCity) filters.push(`City: ${selectedCity}`);
        if (selectedArea) filters.push(`Area: ${selectedArea}`);
      }
      return filters;
    };

    return (
      <div className="pivot-table-container">
        <h4>🏠 Rental Listings Analysis - Average Rent by Property Type & Bedrooms</h4>
        
        <div className="analysis-summary">
          <div className="summary-stats">
            <span><strong>Total Properties:</strong> {pivotData.total_listings}</span>
            <span><strong>Property Types:</strong> {rowEntries.length}</span>
            <span><strong>Bedroom Types:</strong> {bedrooms.length}</span>
            {getFilterSummary().length > 0 && (
              <span><strong>Filters:</strong> {getFilterSummary().join(', ')}</span>
            )}
          </div>
        </div>

        <div className="pivot-table-wrapper">
          <table className="pivot-table">
            <thead>
              <tr>
                <th className="corner-cell">Property Type \ Bedrooms</th>
                {bedrooms.map(bedroom => (
                                      <th key={bedroom} className="structure-header">
                      {bedroom === '5+' ? '5+ Bedrooms' : `${bedroom} ${bedroom === '1' ? 'Bedroom' : 'Bedrooms'}`}
                    </th>
                ))}
                <th className="total-header">Property Type Total</th>
              </tr>
            </thead>
            <tbody>
              {rowEntries.map(row => (
                <tr key={row.key}>
                  <td className="property-type-header">{row.label}</td>
                  {bedrooms.map(bedroom => {
                    const cellData = row.bedrooms[bedroom];
                    return (
                      <td 
                        key={`${row.key}-${bedroom}`} 
                        className={`data-cell ${cellData && cellData.count > 0 ? 'clickable-cell' : ''}`}
                        onClick={() => handleCellClick(row.property_type, bedroom, cellData)}
                        title={cellData && cellData.count > 0 ? 'Click to view listings' : ''}
                      >
                        {cellData ? (
                          <div className="cell-content">
                            <div className="avg-rent">${Math.round(cellData.average_rent).toLocaleString()}</div>
                            <div className="count">({cellData.count} listings)</div>
                            <div className="range">
                              ${Math.round(cellData.min_rent).toLocaleString()} - ${Math.round(cellData.max_rent).toLocaleString()}
                            </div>
                            {cellData.count > 0 && (
                              <div className="click-hint">Click to view listings</div>
                            )}
                          </div>
                        ) : (
                          <div className="no-data">-</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="total-cell">
                    {rowTotals[row.key]?.count > 0 ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(rowTotals[row.key].average).toLocaleString()}</div>
                        <div className="count">({rowTotals[row.key].count} total)</div>
                      </div>
                    ) : (
                      <div className="no-data">-</div>
                    )}
                  </td>
                </tr>
              ))}
              
              {/* Bedroom Totals Row */}
              <tr className="totals-row">
                <td className="total-header">Bedroom Total</td>
                {bedrooms.map(bedroom => (
                  <td key={`total-${bedroom}`} className="total-cell">
                    {bedroomTotals[bedroom]?.count > 0 ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(bedroomTotals[bedroom].average).toLocaleString()}</div>
                        <div className="count">({bedroomTotals[bedroom].count} total)</div>
                      </div>
                    ) : (
                      <div className="no-data">-</div>
                    )}
                  </td>
                ))}
                <td className="grand-total-cell">
                  <div className="cell-content">
                    <div className="avg-rent">${Math.round(grandTotal.average).toLocaleString()}</div>
                    <div className="count">({grandTotal.count} grand total)</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const PivotTable = ({ analysisData }) => {
    if (!analysisData || !analysisData.analysis || !analysisData.analysis.pivot_table) {
      return <div>No pivot table data available</div>;
    }

    const { pivot_table, structure_types, cities } = analysisData.analysis;

    // Calculate totals
    const calculateTotals = () => {
      const cityTotals = {};
      const structureTotals = {};
      let grandTotal = { count: 0, totalRent: 0, properties: [] };

      // Initialize structure totals
      structure_types.forEach(type => {
        structureTotals[type] = { count: 0, totalRent: 0, properties: [] };
      });

      // Calculate city and structure totals
      cities.forEach(city => {
        cityTotals[city] = { count: 0, totalRent: 0, properties: [] };
        
        structure_types.forEach(type => {
          const cellData = pivot_table[city]?.[type];
          if (cellData && cellData.count > 0) {
            const totalRentForCell = cellData.average_rent * cellData.count;
            
            // Add to city total
            cityTotals[city].count += cellData.count;
            cityTotals[city].totalRent += totalRentForCell;
            cityTotals[city].properties.push(...(Array(cellData.count).fill(cellData.average_rent)));
            
            // Add to structure total
            structureTotals[type].count += cellData.count;
            structureTotals[type].totalRent += totalRentForCell;
            structureTotals[type].properties.push(...(Array(cellData.count).fill(cellData.average_rent)));
            
            // Add to grand total
            grandTotal.count += cellData.count;
            grandTotal.totalRent += totalRentForCell;
            grandTotal.properties.push(...(Array(cellData.count).fill(cellData.average_rent)));
          }
        });
        
        // Calculate average for city
        if (cityTotals[city].count > 0) {
          cityTotals[city].average = cityTotals[city].totalRent / cityTotals[city].count;
        }
      });

      // Calculate averages for structures
      structure_types.forEach(type => {
        if (structureTotals[type].count > 0) {
          structureTotals[type].average = structureTotals[type].totalRent / structureTotals[type].count;
        }
      });

      // Calculate grand total average
      if (grandTotal.count > 0) {
        grandTotal.average = grandTotal.totalRent / grandTotal.count;
      }

      return { cityTotals, structureTotals, grandTotal };
    };

    const { cityTotals, structureTotals, grandTotal } = calculateTotals();

    return (
      <div className="pivot-table-container">
        <h4>📊 Toronto Area Residential Rental Analysis - Average Rent by City & Structure Type</h4>
        
        <div className="analysis-summary">
          <div className="summary-stats">
            <span><strong>Total Properties Analyzed:</strong> {analysisData.analysis.total_properties}</span>
            <span><strong>Structure Types:</strong> {analysisData.analysis.summary?.unique_structure_types}</span>
            <span><strong>Cities Found:</strong> {analysisData.analysis.summary?.unique_cities}</span>
            <span><strong>Cities:</strong> {analysisData.analysis.summary?.cities_list}</span>
          </div>
        </div>

        <div className="pivot-table-wrapper">
          <table className="pivot-table">
            <thead>
              <tr>
                <th className="corner-cell">City \ Structure Type</th>
                {structure_types.map(structureType => (
                  <th key={structureType} className="structure-header">
                    {structureType}
                  </th>
                ))}
                <th className="total-header">City Total</th>
              </tr>
            </thead>
            <tbody>
              {cities.map(city => (
                <tr key={city}>
                  <td className="city-header">{city}</td>
                  {structure_types.map(structureType => {
                    const cellData = pivot_table[city]?.[structureType];
                    return (
                      <td 
                        key={`${city}-${structureType}`} 
                        className={`data-cell ${cellData && cellData.listing_keys && cellData.listing_keys.length > 0 ? 'clickable-cell' : ''}`}
                        onClick={() => handleCellClick(city, structureType, cellData)}
                        title={cellData && cellData.listing_keys && cellData.listing_keys.length > 0 ? 'Click to view listing IDs' : ''}
                      >
                        {cellData ? (
                          <div className="cell-content">
                            <div className="avg-rent">${cellData.average_rent.toLocaleString()}</div>
                            <div className="count">({cellData.count} listings)</div>
                            <div className="range">
                              ${cellData.min_rent.toLocaleString()} - ${cellData.max_rent.toLocaleString()}
                            </div>
                            {cellData.listing_keys && cellData.listing_keys.length > 0 && (
                              <div className="click-hint">Click to view IDs</div>
                            )}
                          </div>
                        ) : (
                          <div className="no-data">-</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="total-cell">
                    {cityTotals[city]?.count > 0 ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(cityTotals[city].average).toLocaleString()}</div>
                        <div className="count">({cityTotals[city].count} total)</div>
                      </div>
                    ) : (
                      <div className="no-data">-</div>
                    )}
                  </td>
                </tr>
              ))}
              
              {/* Structure Type Totals Row */}
              <tr className="totals-row">
                <td className="total-header">Structure Total</td>
                {structure_types.map(type => (
                  <td key={`total-${type}`} className="total-cell">
                    {structureTotals[type]?.count > 0 ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(structureTotals[type].average).toLocaleString()}</div>
                        <div className="count">({structureTotals[type].count} total)</div>
                      </div>
                    ) : (
                      <div className="no-data">-</div>
                    )}
                  </td>
                ))}
                <td className="grand-total-cell">
                  <div className="cell-content">
                    <div className="avg-rent">${Math.round(grandTotal.average).toLocaleString()}</div>
                    <div className="count">({grandTotal.count} grand total)</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {analysisData.analysis.raw_data_sample && (
          <div className="raw-data-sample">
            <h5>📋 Sample Raw Data (First 10 properties)</h5>
            <div className="sample-data-table">
              <table>
                <thead>
                  <tr>
                    <th>Listing Key</th>
                    <th>Bedrooms</th>
                    <th>Structure Type</th>
                    <th>Rent</th>
                    <th>City</th>
                  </tr>
                </thead>
                <tbody>
                  {analysisData.analysis.raw_data_sample.slice(0, 10).map((property, index) => (
                    <tr key={index}>
                      <td>{property.listing_key}</td>
                      <td>{property.bedrooms}</td>
                      <td>{property.structure_type}</td>
                      <td>${property.rent.toLocaleString()}</td>
                      <td>{property.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };





  const PhotoGallery = ({ photos, listingId }) => {
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(false);

    if (!photos || photos.length === 0) {
      return (
        <div className="no-photos">
          <div className="no-photos-placeholder">
            🏠 No photos available
          </div>
        </div>
      );
    }

    const displayPhotos = photos.slice(0, 8); // Show up to 8 photos

    const nextPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev + 1) % displayPhotos.length);
      setImageLoaded(false);
    };

    const prevPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);
      setImageLoaded(false);
    };

    const currentPhoto = displayPhotos[currentPhotoIndex];

    return (
      <div className="photo-gallery">
        <div className="main-photo-container">
          <div className="photo-counter">
            {currentPhotoIndex + 1} / {displayPhotos.length}
            {photos.length > 8 && <span className="more-photos"> (+{photos.length - 8} more)</span>}
          </div>
          
          <div className="main-photo">
            {!imageLoaded && <div className="photo-loading">Loading...</div>}
            <img 
              src={currentPhoto.url} 
              alt={currentPhoto.description || `Photo ${currentPhotoIndex + 1}`}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.style.display = 'none';
                setImageLoaded(true);
              }}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
            
            {displayPhotos.length > 1 && (
              <>
                <button className="photo-nav prev" onClick={prevPhoto} aria-label="Previous photo">
                  ‹
                </button>
                <button className="photo-nav next" onClick={nextPhoto} aria-label="Next photo">
                  ›
                </button>
              </>
            )}
          </div>

          {currentPhoto.description && (
            <div className="photo-description">
              {currentPhoto.description}
            </div>
          )}
        </div>

        {displayPhotos.length > 1 && (
          <div className="photo-thumbnails">
            {displayPhotos.map((photo, index) => (
              <button
                key={photo.order || index}
                className={`thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
                onClick={() => {
                  setCurrentPhotoIndex(index);
                  setImageLoaded(false);
                }}
              >
                <img 
                  src={photo.url} 
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ddf-test-page">
      {/* Header */}
      <header className="ddf-header">
        <div className="header-container">
          <Logo size="medium" showText={true} />
          <div className="header-title">
            <h1>DDF Test - Toronto Rental Listings</h1>
            <p>Testing Real Estate Data Distribution Facility Integration</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="ddf-main">
        <div className="container">
          {/* Controls */}
          <div className="ddf-controls">
            <button 
              onClick={fetchDDFListings} 
              disabled={loading}
              className="fetch-button"
            >
              {loading ? 'Fetching...' : 'Refresh Listings'}
            </button>
            
            <button 
              onClick={fetchFullDatabaseAnalysis} 
              disabled={analysisLoading}
              className="fetch-button analysis-button"
            >
              {analysisLoading ? 'Analyzing Full Database...' : 'Fetch Full DDF Analysis'}
            </button>
            
            {fetchCount > 0 && (
              <div className="fetch-info">
                Found {fetchCount} active rental listings in Toronto
                <br />
                <small style={{color: '#888', fontStyle: 'italic'}}>
                  {responseData?.note || 'Real DDF data attempted. Check logs for authentication status.'}
                </small>
                {responseData?.auth_status && (
                  <div style={{marginTop: '8px', fontSize: '12px'}}>
                    <strong>OAuth2 Status:</strong> <span style={{color: responseData.auth_status === 'Success' ? '#28a745' : '#dc3545'}}>{responseData.auth_status}</span>
                    {responseData?.access_token && responseData.access_token !== 'No token' && (
                      <div>
                        <strong>Access Token:</strong> <code style={{backgroundColor: '#f8f9fa', padding: '2px 4px', borderRadius: '3px'}}>{responseData.access_token}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Listing Details Pivot Table Section */}
          <div className="listing-details-section">
            <h3>🏠 Rental Listings Database Analysis</h3>
            <p>Interactive pivot table showing rental listings from our database by city and number of bedrooms.</p>
            
            {/* Enhanced Filtering Controls */}
            <div className="enhanced-filters">
              <h4>🔍 Filter Listings</h4>
              


              {/* Postal Code Text Filter */}
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="postal-code-input">
                    <strong>🏢 Option 1: Filter by Postal Code (First 3 chars):</strong>
                  </label>
                  <input
                    id="postal-code-input"
                    type="text"
                    value={postalCodeFilter}
                    onChange={handlePostalCodeChange}
                    placeholder="e.g. M5V, M2N, L3R"
                    maxLength="3"
                    disabled={pivotLoading}
                    className="postal-code-input"
                    style={{textTransform: 'uppercase'}}
                  />
                  <small className="filter-hint">
                    {postalCodeFilter && postalCodeFilter.trim().length >= 3 ? 
                      '✅ Postal code filter active - City/Area filters disabled' : 
                      'Enter 3 characters to auto-filter'
                    }
                  </small>
                </div>
              </div>

              {/* Two-Level Dropdowns: City and Area */}
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="city-select">
                    <strong>🌍 Option 2: Filter by City & Area:</strong>
                  </label>
                  <select 
                    id="city-select"
                    value={selectedCity}
                    onChange={handleCityChange}
                    disabled={pivotLoading || (postalCodeFilter && postalCodeFilter.trim().length >= 3)}
                  >
                    <option value="">All Cities</option>
                    {availableCities.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="area-select">
                    <strong>Area/Neighborhood:</strong>
                  </label>
                  <select 
                    id="area-select"
                    value={selectedArea}
                    onChange={handleAreaChange}
                    disabled={pivotLoading || !selectedCity || (postalCodeFilter && postalCodeFilter.trim().length >= 3)}
                  >
                    <option value="">All Areas</option>
                    {availableAreas.map(area => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                  {postalCodeFilter && postalCodeFilter.trim().length >= 3 ? (
                    <small className="filter-hint">Disabled when postal code is entered</small>
                  ) : !selectedCity ? (
                    <small className="filter-hint">Select a city first</small>
                  ) : null}
                </div>
              </div>

              {/* Filter Actions */}
              <div className="filter-actions">
                <button 
                  onClick={() => fetchListingDetailsPivot()} 
                  disabled={pivotLoading}
                  className="fetch-button primary"
                >
                  {pivotLoading ? 'Loading...' : 'Refresh Data'}
                </button>
                
                <button 
                  onClick={clearAllFilters} 
                  disabled={pivotLoading}
                  className="fetch-button secondary"
                >
                  Clear All Filters
                </button>
              </div>
            </div>

            {/* Pivot Loading State */}
            {pivotLoading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading pivot table data from database...</p>
              </div>
            )}

            {/* Pivot Error Display */}
            {pivotError && (
              <div className="error-message">
                <h4>Error:</h4>
                <p>{pivotError}</p>
                <button 
                  onClick={() => fetchListingDetailsPivot()}
                  className="retry-button"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Pivot Table Display */}
            {!pivotLoading && !pivotError && pivotData && (
              <ListingDetailsPivotTable />
            )}
          </div>

          {/* Raw API Response Section */}
          {responseData && (
            <div className="api-response-section">
              <h3>📊 Raw DDF Search API Response</h3>
              <p>Complete JSON response from the Toronto residential rental search endpoint:</p>
              
              <div className="json-response">
                <div className="json-header">
                  <h5>📋 Complete JSON Response:</h5>
                  <button 
                    className="copy-button"
                    onClick={(event) => {
                      navigator.clipboard.writeText(JSON.stringify(responseData, null, 2));
                      // Show a brief feedback
                      const button = event.target;
                      const originalText = button.textContent;
                      button.textContent = '✅ Copied!';
                      setTimeout(() => {
                        button.textContent = originalText;
                      }, 2000);
                    }}
                  >
                    📋 Copy Full Response JSON
                  </button>
                </div>
                <div className="json-container">
                  <pre dangerouslySetInnerHTML={{
                    __html: formatJSON(responseData)
                  }}></pre>
                </div>
              </div>

              <div className="response-summary">
                <h5>📈 Response Summary:</h5>
                <div className="summary-grid">
                  <div className="summary-item">
                    <strong>Total Listings:</strong> {responseData.count || 0}
                  </div>
                  <div className="summary-item">
                    <strong>Data Source:</strong> {responseData.data_source || 'Unknown'}
                  </div>
                  <div className="summary-item">
                    <strong>Auth Status:</strong> <span style={{color: responseData.auth_status?.includes('Success') ? '#28a745' : '#dc3545'}}>{responseData.auth_status || 'Unknown'}</span>
                  </div>
                  <div className="summary-item">
                    <strong>API Specification:</strong> {responseData.api_specification || 'Not provided'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Full Database Analysis Section */}
          {(analysisLoading || analysisError || analysisData || analysisProgress) && (
            <div className="analysis-section">
              <h3>📊 Full DDF Database Analysis</h3>
              <p>Complete analysis of Toronto residential rentals from the entire DDF PropertyReplication endpoint</p>
              
              {/* Analysis Loading State with Progress */}
              {analysisLoading && (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  
                  {/* Progress Information */}
                  {analysisProgress ? (
                    <div className="analysis-progress">
                      <h4>🔄 Real-time Progress</h4>
                      <div className="progress-info">
                        <div className="progress-status">
                          <strong>Status:</strong> 
                          <span className={`status-indicator status-${analysisProgress.status}`}>
                            {analysisProgress.status?.toUpperCase()}
                          </span>
                        </div>
                        
                        {analysisProgress.total_fetched > 0 && (
                          <div className="progress-stats">
                            <div className="stat-item">
                              <strong>Properties Fetched:</strong> {analysisProgress.total_fetched.toLocaleString()}
                            </div>
                            <div className="stat-item">
                              <strong>Current Batch:</strong> #{analysisProgress.current_batch}
                            </div>
                            {analysisProgress.estimated_total && (
                              <div className="stat-item">
                                <strong>Estimated Total:</strong> {analysisProgress.estimated_total}
                              </div>
                            )}
                            {analysisProgress.elapsed_seconds && (
                              <div className="stat-item">
                                <strong>Elapsed Time:</strong> {Math.floor(analysisProgress.elapsed_seconds / 60)}m {analysisProgress.elapsed_seconds % 60}s
                              </div>
                            )}
                          </div>
                        )}
                        
                        {analysisProgress.toronto_properties && (
                          <div className="progress-analysis">
                            <div className="stat-item">
                              <strong>Toronto Properties Found:</strong> {analysisProgress.toronto_properties}
                            </div>
                          </div>
                        )}
                        
                        <div className="progress-task-info">
                          <small>
                            <strong>Task ID:</strong> <code>{analysisProgress.task_id}</code><br/>
                            <strong>Current Phase:</strong> {
                              analysisProgress.status === 'initializing' ? 'Starting up...' :
                              analysisProgress.status === 'fetching' ? 'Fetching properties from DDF API...' :
                              analysisProgress.status === 'processing' ? 'Processing and filtering properties...' :
                              analysisProgress.status === 'analyzing' ? 'Creating pivot table analysis...' :
                              analysisProgress.status
                            }
                          </small>
                        </div>
                      </div>
                      
                      {/* Cancel Button */}
                      <button 
                        onClick={cancelAnalysis}
                        className="cancel-button"
                        style={{ marginTop: '10px' }}
                      >
                        Cancel Analysis
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p>Starting DDF database analysis...</p>
                      <small>Initializing connection and starting data fetch operation.</small>
                    </div>
                  )}
                </div>
              )}

              {/* Analysis Error Display */}
              {analysisError && (
                <div className="error-message">
                  <h4>Analysis Error:</h4>
                  <p>{analysisError}</p>
                  <button 
                    onClick={fetchFullDatabaseAnalysis}
                    className="retry-button"
                  >
                    Retry Full Analysis
                  </button>
                </div>
              )}

              {/* Analysis Results Display */}
              {analysisData && (
                <div className="analysis-results">
                  <div className="analysis-completion-info">
                    <h4>✅ Analysis Completed Successfully!</h4>
                    <div className="completion-stats">
                      <div className="stat-item">
                        <strong>Task ID:</strong> <code>{analysisData.task_id}</code>
                      </div>
                      <div className="stat-item">
                        <strong>Total Runtime:</strong> {Math.floor((analysisData.elapsed_time || 0) / 60)}m {(analysisData.elapsed_time || 0) % 60}s
                      </div>
                      <div className="stat-item">
                        <strong>Properties Fetched:</strong> {analysisData.status?.estimated_total || 'Unknown'}
                      </div>
                      <div className="stat-item">
                        <strong>Toronto Properties:</strong> {analysisData.status?.toronto_properties || 'Unknown'}
                      </div>
                      <div className="stat-item">
                        <strong>Properties Analyzed:</strong> {analysisData.status?.analysis?.total_properties || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  {/* Pivot Table */}
                  <PivotTable analysisData={analysisData.status} />

                  {/* Raw API Response */}
                  <div className="analysis-raw-response">
                    <h5>📋 Complete Analysis API Response:</h5>
                    <button 
                      className="copy-button"
                      onClick={(event) => {
                        navigator.clipboard.writeText(JSON.stringify(analysisData, null, 2));
                        const button = event.target;
                        const originalText = button.textContent;
                        button.textContent = '✅ Copied!';
                        setTimeout(() => {
                          button.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      📋 Copy Full Analysis JSON
                    </button>
                    <div className="json-container">
                      <pre dangerouslySetInnerHTML={{
                        __html: formatJSON(analysisData)
                      }}></pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Property Lookup Section */}
          <div className="property-lookup-section">
            <h3>🔍 Property Detail Lookup</h3>
            <p>Enter a DDF Property Key to fetch detailed information from the API:</p>
            
            <div className="property-lookup-controls">
              <input
                type="text"
                value={propertyKey}
                onChange={(e) => setPropertyKey(e.target.value)}
                placeholder="Enter Property Key (e.g., 12345678)"
                className="property-key-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    fetchPropertyDetails();
                  }
                }}
              />
              <button 
                onClick={fetchPropertyDetails} 
                disabled={propertyLoading}
                className="fetch-button"
              >
                {propertyLoading ? 'Fetching...' : 'Get Property Details'}
              </button>
            </div>

            {/* Property Error Display */}
            {propertyError && (
              <div className="error-message">
                <h4>Error:</h4>
                <p>{propertyError}</p>
              </div>
            )}

            {/* Property Loading State */}
            {propertyLoading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Fetching property details from DDF API...</p>
              </div>
            )}

            {/* Property Details Display */}
            {propertyDetails && (
              <div className="property-details-container">
                <h4>✅ Property Details Retrieved</h4>
                <div className="property-info">
                  <p><strong>Property Key:</strong> {propertyDetails.property_id}</p>
                  <p><strong>Available Fields:</strong> {propertyDetails.field_count}</p>
                  <p><strong>API Status:</strong> <span style={{color: '#28a745'}}>{propertyDetails.auth_status}</span></p>
                </div>

                <div className="json-response">
                  <div className="json-header">
                    <h5>📋 Raw JSON Response:</h5>
                    <button 
                      className="copy-button"
                      onClick={(event) => {
                        navigator.clipboard.writeText(JSON.stringify(propertyDetails.property_details, null, 2));
                        // Show a brief feedback
                        const button = event.target;
                        const originalText = button.textContent;
                        button.textContent = '✅ Copied!';
                        setTimeout(() => {
                          button.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      📋 Copy JSON
                    </button>
                  </div>
                  <div className="json-container">
                    <pre dangerouslySetInnerHTML={{
                      __html: formatJSON(propertyDetails.property_details)
                    }}></pre>
                  </div>
                </div>

                <div className="available-fields">
                  <h5>📝 Available Data Fields:</h5>
                  <div className="fields-grid">
                    {propertyDetails.available_fields && propertyDetails.available_fields.map((field) => (
                      <span key={field} className="field-tag">{field}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Agent Details Section */}
            {propertyDetails?.property_details?.ListAgentKey && (
              <div className="agent-section">
                <h3>👤 Listing Agent Information</h3>
                <p>Agent Key: <code>{propertyDetails.property_details.ListAgentKey}</code></p>

                {/* Agent Loading State */}
                {agentLoading && (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Fetching agent details from DDF API...</p>
                  </div>
                )}

                {/* Agent Error Display */}
                {agentError && (
                  <div className="error-message">
                    <h4>Error:</h4>
                    <p>{agentError}</p>
                    <button 
                      onClick={() => fetchAgentDetails(propertyDetails.property_details.ListAgentKey)}
                      className="retry-button"
                    >
                      Retry Agent Lookup
                    </button>
                  </div>
                )}

                {/* Agent Details Display */}
                {agentDetails && (
                  <div className="agent-details-container">
                    <h4>✅ Agent Details Retrieved</h4>
                    
                    {/* Agent Summary Card */}
                    <div className="agent-summary-card">
                      {/* Agent Photo */}
                      {(() => {
                        // Extract photo URL from Media array
                        const media = agentDetails.member_details?.Media;
                        let photoUrl = null;
                        
                        if (Array.isArray(media) && media.length > 0) {
                          // Find the first media item with a MediaURL
                          const photoMedia = media.find(item => item.MediaURL);
                          photoUrl = photoMedia?.MediaURL;
                        }
                        
                        return photoUrl ? (
                          <div className="agent-photo-container">
                            <img 
                              src={photoUrl} 
                              alt={`${agentDetails.member_details?.MemberFirstName || ''} ${agentDetails.member_details?.MemberLastName || ''}`.trim() || 'Agent Photo'}
                              className="agent-photo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ) : null;
                      })()}
                      
                      <div className="agent-info-grid">
                        <div className="agent-info-item">
                          <strong>First Name:</strong> {agentDetails.member_details?.MemberFirstName || 'Not available'}
                        </div>
                        <div className="agent-info-item">
                          <strong>Last Name:</strong> {agentDetails.member_details?.MemberLastName || 'Not available'}
                        </div>
                        <div className="agent-info-item">
                          <strong>Full Name:</strong> {
                            agentDetails.member_details?.MemberFirstName && agentDetails.member_details?.MemberLastName 
                              ? `${agentDetails.member_details.MemberFirstName} ${agentDetails.member_details.MemberLastName}`
                              : agentDetails.member_details?.MemberFullName || 'Not available'
                          }
                        </div>
                        <div className="agent-info-item">
                          <strong>Office Phone:</strong> {agentDetails.member_details?.MemberOfficePhone || 'Not available'}
                        </div>
                        <div className="agent-info-item">
                          <strong>Email:</strong> {agentDetails.member_details?.MemberEmail || 'Not available'}
                        </div>
                        <div className="agent-info-item">
                          <strong>License:</strong> {agentDetails.member_details?.MemberStateLicense || 'Not available'}
                        </div>
                      </div>
                    </div>

                    {/* Agent JSON Response */}
                    <div className="json-response">
                      <div className="json-header">
                        <h5>📋 Raw Agent JSON Response:</h5>
                        <button 
                          className="copy-button"
                          onClick={(event) => {
                            navigator.clipboard.writeText(JSON.stringify(agentDetails.member_details, null, 2));
                            // Show a brief feedback
                            const button = event.target;
                            const originalText = button.textContent;
                            button.textContent = '✅ Copied!';
                            setTimeout(() => {
                              button.textContent = originalText;
                            }, 2000);
                          }}
                        >
                          📋 Copy Agent JSON
                        </button>
                      </div>
                      <div className="json-container">
                        <pre dangerouslySetInnerHTML={{
                          __html: formatJSON(agentDetails.member_details)
                        }}></pre>
                      </div>
                    </div>

                    {/* Agent Available Fields */}
                    <div className="available-fields">
                      <h5>📝 Available Agent Data Fields:</h5>
                      <div className="fields-grid">
                        {agentDetails.available_fields && agentDetails.available_fields.map((field) => (
                          <span key={field} className="field-tag">{field}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <h3>Error:</h3>
              <p>{error}</p>
              <button onClick={fetchDDFListings} className="retry-button">
                Try Again
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Connecting to DDF and fetching listings...</p>
            </div>
          )}

          {/* Cell Listings Loading State */}
          {cellListingsLoading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Fetching detailed property information for selected cell...</p>
            </div>
          )}

          {/* Selected Cell Info Header */}
          {selectedCellInfo && listings.length > 0 && (
            <div className="selected-cell-header">
              <h3>🏠 Properties from Pivot Table Selection</h3>
              <div className="cell-selection-info">
                <div className="selection-badge">
                  <span className="property-type-name">{selectedCellInfo.propertyType}</span>
                  <span className="separator">•</span>
                  <span className="structure-type">
                    {selectedCellInfo.bedroomCount === '5+' ? '5+ Bedrooms' : `${selectedCellInfo.bedroomCount} ${selectedCellInfo.bedroomCount === '1' ? 'Bedroom' : 'Bedrooms'}`}
                  </span>
                  <span className="separator">•</span>
                  <span className="location-filter">
                    {postalCodeFilter && postalCodeFilter.trim().length >= 3 ? 
                      `📮 ${postalCodeFilter.trim().toUpperCase()}` : 
                      selectedCity && selectedArea ? `🌍 ${selectedCity} - ${selectedArea}` :
                      selectedCity ? `🌍 ${selectedCity}` : '🌍 All Areas'
                    }
                  </span>
                </div>
                <div className="selection-stats">
                  <span className="stat">📊 {selectedCellInfo.count} total properties</span>
                  <span className="stat">💰 Avg: ${Math.round(selectedCellInfo.averageRent).toLocaleString()}</span>
                  <span className="stat">📈 Range: ${Math.round(selectedCellInfo.minRent).toLocaleString()} - ${Math.round(selectedCellInfo.maxRent).toLocaleString()}</span>
                  <span className="stat">🔍 Showing filtered listings</span>
                </div>
              </div>
            </div>
          )}

          {/* Listings Grid */}
          {!loading && !error && listings.length > 0 && (
            <div className="listings-grid">
              {listings.map((listing, index) => (
                <div key={listing.id || index} className="listing-card compact">
                  {/* Photo Gallery */}
                  <PhotoGallery photos={listing.photos} listingId={listing.id} />
                  
                  {/* Compact Content */}
                  <div className="listing-content">
                    {/* Price and Realtor Logo - aligned on same row */}
                    <div className="compact-price-header">
                      <div className="compact-price">
                        {formatPrice(listing.price)}
                        {listing.price && <span className="price-period">/month</span>}
                      </div>
                      {listing.listing_url ? (
                        <a 
                          href={listing.listing_url.startsWith('http') ? listing.listing_url : `https://${listing.listing_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="compact-realtor-link"
                          title="View on REALTOR.ca"
                        >
                          <img 
                            width="60" 
                            src="https://www.realtor.ca/images/en-ca/powered_by_realtor.svg" 
                            alt="REALTOR.ca"
                          />
                        </a>
                      ) : (
                        <a 
                          href="https://www.realtor.ca/en"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="compact-realtor-link"
                          title="Visit REALTOR.ca"
                        >
                          <img 
                            width="60" 
                            src="https://www.realtor.ca/images/en-ca/powered_by_realtor.svg" 
                            alt="REALTOR.ca"
                          />
                        </a>
                      )}
                    </div>

                    {/* Address - left aligned with postal code */}
                    <div className="compact-address-section">
                      <h3 className="compact-address">
                        {listing.address || 'Address not available'}
                        {listing.postal_code && ` ${listing.postal_code}`}
                      </h3>
                    </div>

                    {/* Location - area, city, province format */}
                    <div className="compact-location">
                      📍 {(() => {
                        const parts = [];
                        if (listing.neighborhood) parts.push(listing.neighborhood);
                        if (listing.city) parts.push(listing.city);
                        if (listing.province) parts.push(listing.province);
                        return parts.length > 0 ? parts.join(', ') : 'Location not available';
                      })()}
                    </div>

                    {/* Property Info Icons - including bathrooms, parking, and square footage */}
                    <div className="compact-property-info">
                      <div className="info-icon">
                        🏠 <span>{listing.property_type || 'N/A'}</span>
                      </div>
                      {listing.bedrooms && (
                        <div className="info-icon">
                          🛏️ <span>{listing.bedrooms}</span>
                        </div>
                      )}
                      {listing.bathrooms && (
                        <div className="info-icon">
                          🚿 <span>{listing.bathrooms}</span>
                        </div>
                      )}
                      {listing.parking_spaces && (
                        <div className="info-icon">
                          🅿️ <span>{listing.parking_spaces}</span>
                        </div>
                      )}
                      {listing.size_interior && (
                        <div className="info-icon">
                          📏 <span>{Math.round(listing.size_interior).toLocaleString()} sqft</span>
                        </div>
                      )}
                    </div>

                    {/* Compact Description */}
                    {listing.description && (
                      <div className="compact-description">
                        {listing.description.substring(0, 120)}
                        {listing.description.length > 120 && '...'}
                      </div>
                    )}

                    {/* Agent & Brokerage */}
                    {listing.agent_details && (
                      <div className="compact-agent">
                        <div className="agent-info-compact">
                          {listing.agent_details.photo_url && (
                            <img 
                              src={listing.agent_details.photo_url} 
                              alt={listing.agent_details.name || 'Agent'}
                              className="agent-photo-small"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNjY2MiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMTJjMi4yMSAwIDQtMS43OSA0LTRzLTEuNzktNC00LTQtNCAxLjc5LTQgNCAxLjc5IDQgNCA0em0wIDJjLTIuNjcgMC04IDEuMzQtOCA0djJoMTZ2LTJjMC0yLjY2LTUuMzMtNC04LTR6Ii8+PC9zdmc+';
                              }}
                            />
                          )}
                          <div className="agent-text">
                            <div className="agent-name-compact">{listing.agent_details.name}</div>
                            {listing.agent_details.office_name && (
                              <div className="agent-office-compact">{listing.agent_details.office_name}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bottom Row: MLS ID, DDF ID, and Days on Market */}
                    <div className="compact-footer">
                      <div className="footer-left">
                        {(listing.listing_id || listing.mls) && (
                          <span className="compact-mls">MLS®: {listing.listing_id || listing.mls}</span>
                        )}
                        {(listing.ddf_listing_id || listing.origin_post_id || listing.id) && (
                          <span className="compact-ddf">ID: {listing.ddf_listing_id || listing.origin_post_id || listing.id}</span>
                        )}
                      </div>
                      <div className="footer-right">
                        {listing.date_listed && (
                          <span className="compact-days-on-market">
                            Days on Market: {(() => {
                              const days = Math.floor((new Date() - new Date(listing.date_listed)) / (1000 * 60 * 60 * 24));
                              return days === 0 ? '0 Days' : `${days} Days`;
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Listings */}
          {!loading && !error && listings.length === 0 && (
            <div className="no-listings">
              <h3>No listings found</h3>
              <p>No active rental listings were found in Toronto. This could be due to:</p>
              <ul>
                <li>DDF authentication issues</li>
                <li>No rental properties currently available</li>
                <li>Query filters being too restrictive</li>
              </ul>
            </div>
          )}

          {/* Technical Info */}
          <div className="technical-info">
            <h4>Technical Details:</h4>
            <div className="tech-details">
              <div><strong>DDF Endpoint:</strong> https://data.crea.ca</div>
              <div><strong>Query:</strong> Active rental properties in Toronto</div>
              <div><strong>Property Types:</strong> Single Family Attached, Single Family Detached, Apartment, Condominium</div>
              <div><strong>Lambda Function:</strong> tenanthelpai-function v393</div>
              <div><strong>Last Updated:</strong> {new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DDFTestPage; 
